import { createContext, useContext, useState, useEffect, useRef, useCallback, type ReactNode } from 'react';
import {
  pb,
  currentSession,
  extractMfaId,
  pbErrorMessage,
  USERS_COLLECTION,
  type Session,
} from '../services/pocketbase';
import type { UserProfile, Organization, PlanFeatures, PlanType, AnalysisType } from '../../shared/types';
import { PLAN_FEATURES, BUNDLES } from '../../shared/types';

interface AuthContextType {
  user: UserProfile | null;
  organization: Organization | null;
  features: PlanFeatures;
  session: Session | null;
  isLoading: boolean;
  /** True when the password step succeeded but PocketBase still demands a second factor (email OTP). */
  mfaRequired: boolean;
  /** Super admin only: simulate a plan for UI preview (null = real view) */
  previewPlan: PlanType | null;
  setPreviewPlan: (plan: PlanType | null) => void;
  /** Super admin only: simulate the post-signup state (no sub, no credits, no choice made yet) */
  previewOnboarding: boolean;
  setPreviewOnboarding: (value: boolean) => void;
  /** Super admin only: simulate a "bought Pack Lancement bundle" profile (limited per-type credits). */
  previewBundle: boolean;
  setPreviewBundle: (value: boolean) => void;
  /** Decrement a simulated bundle credit (preview only) — lets the admin see the limit shrink in real time. */
  consumePreviewCredit: (type: AnalysisType) => void;
  /** True when this org is a fresh signup that hasn't subscribed nor bought a bundle. */
  isOnboarding: boolean;
  /** True after the user follows a password-recovery link (PocketBase reset token present in the URL). Gates the app to a "set new password" view. */
  recoveryMode: boolean;
  /** Last Google OAuth failure — the popup flow can't return an error through `signInWithGoogle`'s void signature. */
  oauthError: string | null;
  signIn: (email: string, password: string) => Promise<{ error?: string; requiresMfa?: boolean }>;
  signInWithGoogle: () => Promise<void>;
  signUp: (
    email: string,
    password: string,
    termsAccepted: { version: string; acceptedAt: string },
  ) => Promise<{ error?: string }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error?: string }>;
  /** Set a new password for the user currently in recovery mode, then clear the gate. */
  updatePassword: (newPassword: string) => Promise<{ error?: string }>;
  refreshProfile: () => Promise<void>;
  /** Verify the one-time code emailed as the second factor, and finish signing in. */
  verifyMfaChallenge: (code: string) => Promise<{ error?: string }>;
  /** Send a fresh one-time code for the pending MFA challenge. */
  resendMfaCode: () => Promise<{ error?: string }>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function useAuth(): AuthContextType {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

interface MeResponse extends UserProfile {
  organization: Organization | null;
}

const API_URL = import.meta.env.VITE_API_URL ?? '';

async function fetchMe(accessToken: string): Promise<MeResponse | null> {
  try {
    const res = await fetch(`${API_URL}/api/auth/me`, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
    if (!res.ok) return null;
    return await res.json();
  } catch {
    return null;
  }
}

/**
 * Récupère le jeton de réinitialisation de mot de passe présent dans l'URL.
 *
 * Différence majeure avec Supabase : le lien de reset Supabase ouvrait une
 * VRAIE session (hash `#access_token=…` capté par `detectSessionInUrl`), d'où
 * l'événement `PASSWORD_RECOVERY`. Le lien PocketBase ne contient qu'un jeton
 * à usage unique qui n'authentifie PAS — il ne sert qu'à
 * `confirmPasswordReset()`. Il n'y a donc pas de session à garder, et le
 * « mode récupération » est simplement « un jeton de reset est en main ».
 *
 * Deux formes acceptées :
 *   • `/reset-password?token=…`  → template d'email personnalisé (recommandé)
 *   • `…#/auth/confirm-password-reset/…` → template PocketBase par défaut
 *
 * Le jeton est retiré de l'URL immédiatement pour ne pas rester dans
 * l'historique du navigateur. Cette fonction est appelée UNE SEULE FOIS au
 * niveau module : sous React 19 StrictMode, un `useState(initializer)` serait
 * ré-exécuté et le second appel — l'URL ayant déjà été nettoyée — écraserait le
 * jeton par `null`.
 */
function readRecoveryToken(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const fromHash = url.hash.match(/confirm-password-reset\/([^/?#]+)/)?.[1] ?? null;
    const fromQuery = url.pathname.startsWith('/reset-password')
      ? url.searchParams.get('token')
      : null;
    const token = fromQuery ?? fromHash;
    if (!token) return null;

    url.searchParams.delete('token');
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
    return token;
  } catch {
    return null;
  }
}

const INITIAL_RECOVERY_TOKEN = readRecoveryToken();

/** Défi MFA en cours : le `mfaId` rendu par l'étape mot de passe + l'OTP envoyé par email. */
interface MfaPending {
  mfaId: string;
  otpId: string;
  email: string;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<UserProfile | null>(null);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [previewPlan, setPreviewPlanState] = useState<PlanType | null>(null);
  const [previewOnboarding, setPreviewOnboardingState] = useState(false);
  // Crédits simulés d'un acheteur de bundle (preview admin). null = mode inactif.
  const [previewBundleCredits, setPreviewBundleCredits] = useState<Partial<Record<AnalysisType, number>> | null>(null);
  const [mfaRequired, setMfaRequired] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const [recoveryToken, setRecoveryToken] = useState<string | null>(INITIAL_RECOVERY_TOKEN);

  // Le défi MFA vit dans une ref : il ne pilote aucun rendu (c'est `mfaRequired`
  // qui le fait) et on veut y accéder sans le remettre dans les dépendances des
  // callbacks.
  const mfaPendingRef = useRef<MfaPending | null>(null);
  const bootstrappedRef = useRef(false);

  const recoveryMode = recoveryToken !== null;
  const previewBundle = previewBundleCredits !== null;

  // Modes de preview mutuellement exclusifs : activer l'un désactive les autres.
  const setPreviewPlan = useCallback((plan: PlanType | null) => {
    setPreviewPlanState(plan);
    if (plan !== null) { setPreviewOnboardingState(false); setPreviewBundleCredits(null); }
  }, []);
  const setPreviewOnboarding = useCallback((value: boolean) => {
    setPreviewOnboardingState(value);
    if (value) { setPreviewPlanState(null); setPreviewBundleCredits(null); }
  }, []);
  const setPreviewBundle = useCallback((value: boolean) => {
    if (value) {
      const pack = BUNDLES.find((b) => b.id === 'pack_lancement');
      const credits: Partial<Record<AnalysisType, number>> = {};
      pack?.creditLines.forEach((l) => {
        if (!l.analysisType) return;
        credits[l.analysisType] = (credits[l.analysisType] ?? 0) + l.credits;
      });
      setPreviewBundleCredits(credits);
      setPreviewPlanState(null);
      setPreviewOnboardingState(false);
    } else {
      setPreviewBundleCredits(null);
    }
  }, []);
  const consumePreviewCredit = useCallback((type: AnalysisType) => {
    setPreviewBundleCredits((prev) => {
      if (!prev) return prev;
      const cur = prev[type] ?? 0;
      if (cur <= 0) return prev;
      return { ...prev, [type]: cur - 1 };
    });
  }, []);

  // Super admin: use the plan from /api/auth/me (backend overrides to premium)
  // Regular user: use the org plan
  // Preview mode: override with simulated plan (un acheteur de bundle = plan 'free' + crédits)
  const realPlan = user?.plan ?? organization?.plan ?? 'free';
  const effectivePlan = previewBundle ? 'free' : (previewPlan ?? realPlan);

  // Solde de crédits effectif (preview bundle = somme simulée, sinon réel).
  const bundleBalance = previewBundleCredits
    ? Object.values(previewBundleCredits).reduce((a, b) => a + (b ?? 0), 0)
    : 0;
  const creditBalanceEff = previewBundle ? bundleBalance : (organization?.creditBalance ?? 0);

  // Override apiAccess with the per-org gate. The plan provides the
  // ceiling (only Premium+ are eligible at all), the org flag is the
  // actual switch — both must be true. In preview mode we ignore the
  // org flag and trust the simulated plan, so super-admins can still
  // see what the API tab looks like for any plan.
  // Hors preview, un super-admin garde toujours l'accès API : c'est déjà ce
  // que renvoie le backend (`isSuperAdmin || computeApiAccess(...)` dans
  // server/middleware/auth.ts), sans quoi l'onglet API disparaîtrait de son
  // propre compte dès que son org n'a pas le flag `api_access_enabled`.
  const baseFeatures = (previewPlan || previewBundle)
    ? PLAN_FEATURES[effectivePlan]
    : {
        ...PLAN_FEATURES[effectivePlan],
        apiAccess:
          user?.isSuperAdmin === true ||
          (PLAN_FEATURES[effectivePlan].apiAccess && organization?.apiAccessEnabled === true),
      };
  // Un acheteur de crédits/bundle (plan free + crédits) N'EST PAS en lecture
  // seule : il doit pouvoir copier, partager et conserver ses analyses. On
  // débloque donc ces features dès qu'il reste des crédits (réel ou preview).
  const features = creditBalanceEff > 0
    ? { ...baseFeatures, copyText: true, history: true }
    : baseFeatures;

  // When previewing a plan or a bundle, simulate the org's limits to match
  const effectiveOrganization = (previewBundle && organization)
    ? {
        ...organization,
        plan: 'free' as PlanType,
        subscriptionStatus: null,
        freeAnalysesLimit: 0,
        freeAnalysesUsed: 0,
        creditBalance: bundleBalance,
        creditsPerType: previewBundleCredits ?? {},
      }
    : (previewPlan && organization)
    ? {
        ...organization,
        plan: previewPlan,
        freeAnalysesLimit: features.monthlyLimit ?? 0,
        freeAnalysesUsed: 0,
      }
    : organization;

  // Real onboarding state = no active subscription AND no credits.
  // We exclude super admins (who always have premium overridden by the backend)
  // so they can preview the app normally without the onboarding gate triggering.
  const realIsOnboarding = !!(
    organization &&
    !user?.isSuperAdmin &&
    !organization.subscriptionStatus &&
    organization.creditBalance === 0 &&
    organization.plan !== 'pro' &&
    organization.plan !== 'premium' &&
    organization.plan !== 'enterprise' &&
    organization.plan !== 'beta' &&
    // `partner` = accès illimité offert : c'est un compte actif, pas un signup.
    organization.plan !== 'partner'
  );
  const isOnboarding = previewOnboarding || realIsOnboarding;

  const applyProfile = useCallback((me: MeResponse | null) => {
    if (me) {
      const { organization: org, ...profile } = me;
      setUser(profile);
      setOrganization(org);
    } else {
      setUser(null);
      setOrganization(null);
    }
  }, []);

  const loadProfile = useCallback(async (s: Session | null) => {
    applyProfile(s?.token ? await fetchMe(s.token) : null);
  }, [applyProfile]);

  // ── Initialisation + écoute des changements de session ────────────────
  //
  // `pb.authStore.onChange(cb, true)` remplace `supabase.auth.onAuthStateChange` :
  // avec `fireImmediately = true`, le callback part une première fois avec l'état
  // restauré depuis localStorage — l'équivalent exact de l'événement
  // `INITIAL_SESSION` sur lequel s'appuyait l'ancien code.
  //
  // Le contournement `setTimeout(…, 0)` de Supabase n'a plus lieu d'être : il
  // servait à sortir du verrou asynchrone interne de supabase-js, que
  // PocketBase n'a pas (l'authStore est un simple observable synchrone). On
  // garde en revanche un compteur de génération, parce que StrictMode monte
  // l'effet deux fois et que deux `GET /api/auth/me` peuvent revenir dans le
  // désordre.
  useEffect(() => {
    let mounted = true;
    let generation = 0;

    const unsubscribe = pb.authStore.onChange(() => {
      if (!mounted) return;
      const gen = ++generation;
      const s = currentSession();
      setSession(s);

      void (async () => {
        const me = s?.token ? await fetchMe(s.token) : null;
        // Une réponse périmée (une session plus récente est arrivée entre-temps)
        // ne doit jamais écraser le profil courant.
        if (!mounted || gen !== generation) return;
        applyProfile(me);
        setIsLoading(false);
      })();
    }, true);

    return () => {
      mounted = false;
      unsubscribe();
    };
  }, [applyProfile]);

  // Revalidation du jeton restauré depuis localStorage.
  //
  // `authStore.isValid` ne fait que vérifier localement la claim `exp` : il ne
  // détecte ni un compte supprimé, ni un mot de passe changé (qui invalide les
  // jetons côté PocketBase). Contrairement à supabase-js, le SDK ne rafraîchit
  // pas le jeton tout seul — `authRefresh()` fait les deux : il valide auprès du
  // serveur ET prolonge le jeton, ce qui évite de déconnecter un utilisateur
  // actif au bout de la durée de vie du jeton.
  useEffect(() => {
    if (bootstrappedRef.current) return;
    bootstrappedRef.current = true;
    if (!pb.authStore.isValid) return;
    pb.collection(USERS_COLLECTION)
      .authRefresh()
      .catch(() => pb.authStore.clear());
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    setOauthError(null);
    try {
      await pb.collection(USERS_COLLECTION).authWithPassword(email, password);
      // Succès direct : `authStore.onChange` a déjà propagé la session. On
      // recharge tout de même le profil ici pour que l'appelant puisse enchaîner
      // sans flash d'écran de connexion.
      const s = currentSession();
      setSession(s);
      await loadProfile(s);
      return {};
    } catch (err) {
      const mfaId = extractMfaId(err);
      if (!mfaId) {
        return { error: pbErrorMessage(err, 'Email ou mot de passe incorrect.') };
      }

      // Second facteur exigé. PocketBase impose deux méthodes d'authentification
      // DIFFÉRENTES : le mot de passe ayant servi de première, on enchaîne sur
      // le code à usage unique envoyé par email.
      try {
        const { otpId } = await pb.collection(USERS_COLLECTION).requestOTP(email);
        mfaPendingRef.current = { mfaId, otpId, email };
        setMfaRequired(true);
        return { requiresMfa: true };
      } catch (otpErr) {
        return {
          error: pbErrorMessage(otpErr, "Impossible d'envoyer le code de vérification par email."),
        };
      }
    }
  }, [loadProfile]);

  const verifyMfaChallenge = useCallback(async (code: string) => {
    const pending = mfaPendingRef.current;
    if (!pending) {
      return { error: 'Vérification expirée. Reconnectez-vous pour recevoir un nouveau code.' };
    }
    const normalized = code.replace(/\s+/g, '');
    if (normalized.length < 4) {
      return { error: 'Code incomplet.' };
    }
    try {
      // Le `mfaId` du 401 précédent est repassé en paramètre : c'est lui qui
      // relie les deux facteurs côté serveur.
      await pb
        .collection(USERS_COLLECTION)
        .authWithOTP(pending.otpId, normalized, { mfaId: pending.mfaId });
    } catch (err) {
      return { error: pbErrorMessage(err, 'Code invalide ou expiré.') };
    }

    mfaPendingRef.current = null;
    setMfaRequired(false);
    const s = currentSession();
    setSession(s);
    await loadProfile(s);
    return {};
  }, [loadProfile]);

  const resendMfaCode = useCallback(async () => {
    const pending = mfaPendingRef.current;
    if (!pending) {
      return { error: 'Vérification expirée. Reconnectez-vous pour recevoir un nouveau code.' };
    }
    try {
      const { otpId } = await pb.collection(USERS_COLLECTION).requestOTP(pending.email);
      mfaPendingRef.current = { ...pending, otpId };
      return {};
    } catch (err) {
      return { error: pbErrorMessage(err, "Impossible de renvoyer le code par email.") };
    }
  }, []);

  // OAuth2 « tout-en-un » du SDK : ouverture d'une popup vers Google, puis
  // résolution via le canal temps réel de PocketBase. On garde donc l'onglet
  // applicatif intact, là où Supabase faisait une redirection pleine page avec
  // retour sur `window.location.origin`. L'expérience visible est la même
  // (« Continuer avec Google » → compte connecté), en évitant le rechargement.
  //
  // La signature reste `Promise<void>` (contrat public inchangé) : l'échec
  // éventuel — popup bloquée, fenêtre fermée — est exposé via `oauthError`.
  const signInWithGoogle = useCallback(async () => {
    setOauthError(null);
    try {
      // Note : le SDK n'expose pas de query params arbitraires sur l'URL
      // d'autorisation, donc le `prompt: 'select_account'` que passait Supabase
      // n'a pas d'équivalent — Google réutilisera le compte déjà connecté.
      await pb.collection(USERS_COLLECTION).authWithOAuth2({ provider: 'google' });
      const s = currentSession();
      setSession(s);
      await loadProfile(s);
    } catch (err) {
      setOauthError(pbErrorMessage(err, 'Connexion Google annulée ou impossible.'));
    }
  }, [loadProfile]);

  const signUp = useCallback(async (
    email: string,
    password: string,
    termsAccepted: { version: string; acceptedAt: string },
  ) => {
    // La collection `users` est en `createRule: null` (superuser uniquement) :
    // impossible d'appeler `pb.collection('users').create()` depuis le
    // navigateur. L'inscription passe donc par Express, qui crée
    // l'enregistrement avec son jeton superuser, provisionne l'organisation et
    // déclenche l'email de vérification.
    try {
      const res = await fetch(`${API_URL}/api/auth/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email,
          password,
          passwordConfirm: password,
          // Acceptation CGU + Politique de confidentialité : écrites sur
          // `users.terms_accepted_at` / `users.terms_version` par le serveur
          // (ex-trigger `handle_new_user()` de Supabase).
          termsAcceptedAt: termsAccepted.acceptedAt,
          termsVersion: termsAccepted.version,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        return { error: data.error ?? `Inscription impossible (erreur ${res.status}).` };
      }
      return {};
    } catch {
      return { error: "Serveur d'inscription injoignable. Réessayez dans un instant." };
    }
  }, []);

  const signOut = useCallback(async () => {
    // PocketBase est sans session serveur : « se déconnecter » = jeter le jeton.
    // `clear()` déclenche `authStore.onChange`, qui remet session/profil à null.
    pb.authStore.clear();
    setSession(null);
    setUser(null);
    setOrganization(null);
    setPreviewPlanState(null);
    setPreviewOnboardingState(false);
    setPreviewBundleCredits(null);
    setMfaRequired(false);
    setOauthError(null);
    mfaPendingRef.current = null;
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      await pb.collection(USERS_COLLECTION).requestPasswordReset(email);
      return {};
    } catch (err) {
      return { error: pbErrorMessage(err, "Impossible d'envoyer l'email de réinitialisation.") };
    }
  }, []);

  const updatePassword = useCallback(async (newPassword: string) => {
    if (!recoveryToken) {
      return { error: 'Lien de réinitialisation absent ou expiré. Redemandez un email.' };
    }
    try {
      await pb
        .collection(USERS_COLLECTION)
        .confirmPasswordReset(recoveryToken, newPassword, newPassword);
    } catch (err) {
      return {
        error: pbErrorMessage(err, 'Lien expiré ou mot de passe refusé. Redemandez un email de réinitialisation.'),
      };
    }

    // Changer le mot de passe invalide TOUS les jetons existants côté
    // PocketBase (le `tokenKey` de l'enregistrement est régénéré). Contrairement
    // à Supabase, l'utilisateur ne reste donc pas connecté : on purge le store
    // local et il se reconnecte avec son nouveau mot de passe.
    pb.authStore.clear();
    setRecoveryToken(null);
    return {};
  }, [recoveryToken]);

  const refreshProfile = useCallback(async () => {
    await loadProfile(currentSession());
  }, [loadProfile]);

  return (
    <AuthContext.Provider value={{
      user, organization: effectiveOrganization, features, session, isLoading,
      mfaRequired,
      previewPlan, setPreviewPlan,
      previewOnboarding, setPreviewOnboarding,
      previewBundle, setPreviewBundle, consumePreviewCredit,
      isOnboarding,
      recoveryMode,
      oauthError,
      signIn, signInWithGoogle, signUp, signOut, resetPassword, updatePassword, refreshProfile,
      verifyMfaChallenge, resendMfaCode,
    }}>
      {children}
    </AuthContext.Provider>
  );
}
